// «Начать» (#1851, #1854): open the user's tutorial project, seeding one first
// when there is none to open.
//
// The project is a real Film project in the personal space, written through
// the same endpoints the editor writes with. What makes it "the tutorial
// project" is `user.tutorial.project_id` alone, so that id is written LAST,
// once the seed is whole: a stored id always names a complete project, and a
// seed that broke half-way is deleted rather than left looking like one.
//
// The network sits behind `TutorialProjectDeps` so the decision and the order
// of writes can be tested without a query client.

import type { UserTutorial } from '@/types';
import type { TutorialSeedCharacter, TutorialSeedPlan } from './tutorialSeed';

/** What reading a stored project id found. A project in the trash reads as
 *  `missing`: the tutorial never restores one the user threw away. */
export type StoredProjectProbe = 'active' | 'missing';

/** The fresh project's single act and the episodes it came with. */
export interface TutorialProjectStructure {
    /** The backend Episode («АКТ») every act and scene hangs off. */
    episodeId: number;
    actId: number;
    actCode: string;
    /** The act's fragments («эпизоды»), in order. */
    fragmentIds: number[];
}

export interface TutorialSeedSceneWrite {
    episodeId: number;
    fragmentId: number;
    position: number;
    body: string | null;
    tagIds: number[];
}

export interface TutorialProjectDeps {
    readTutorial(): Promise<UserTutorial>;
    writeProjectId(projectId: number): Promise<void>;
    /** Throws when it cannot tell (offline, a 5xx): seeding then would leave
     *  the user a second tutorial project. */
    probeProject(projectId: number): Promise<StoredProjectProbe>;
    /** Creates the project with one act and its first episode; answers its id. */
    createProject(title: string): Promise<number>;
    loadStructure(projectId: number): Promise<TutorialProjectStructure>;
    createFragment(projectId: number, fragment: { actId: number; code: string; order: number }): Promise<number>;
    /** Answers the character's entity-tag id. */
    createCharacter(projectId: number, character: TutorialSeedCharacter): Promise<number>;
    /** Answers the location's entity-tag id. */
    createLocation(projectId: number, name: string): Promise<number>;
    createScene(projectId: number, scene: TutorialSeedSceneWrite): Promise<void>;
    /** Takes a half-seeded project out of the user's projects: for good where
     *  the backend allows it, else to the trash. */
    deleteProject(projectId: number): Promise<void>;
}

/** The seed did not complete. `projectId` is the project it started (`null`
 *  when even the create was refused); `cleanedUp` whether nothing is left of it. */
export class TutorialSeedError extends Error {
    readonly projectId: number | null;
    readonly cleanedUp: boolean;
    constructor(projectId: number | null, cleanedUp: boolean, cause: unknown) {
        super('Tutorial project seed failed', { cause });
        this.name = 'TutorialSeedError';
        this.projectId = projectId;
        this.cleanedUp = cleanedUp;
    }
}

/** Scenes are spaced like the editor spaces new ones. */
const POSITION_STEP = 1000;

export async function openTutorialProject(
    plan: TutorialSeedPlan,
    deps: TutorialProjectDeps,
): Promise<{ projectId: number; seeded: boolean }> {
    const { project_id: storedId } = await deps.readTutorial();
    if (storedId !== null && (await deps.probeProject(storedId)) === 'active') {
        return { projectId: storedId, seeded: false };
    }

    let projectId: number;
    try {
        projectId = await deps.createProject(plan.projectTitle);
    } catch (error) {
        throw new TutorialSeedError(null, true, error);
    }

    try {
        await seedProject(projectId, plan, deps);
        await deps.writeProjectId(projectId);
    } catch (error) {
        const cleanedUp = await deps.deleteProject(projectId).then(
            () => true,
            () => false,
        );
        throw new TutorialSeedError(projectId, cleanedUp, error);
    }
    return { projectId, seeded: true };
}

async function seedProject(projectId: number, plan: TutorialSeedPlan, deps: TutorialProjectDeps): Promise<void> {
    const structure = await deps.loadStructure(projectId);
    const fragmentIds = [...structure.fragmentIds];
    for (let order = fragmentIds.length + 1; order <= plan.episodes.length; order++) {
        fragmentIds.push(await deps.createFragment(projectId, { actId: structure.actId, code: `${structure.actCode}-${order}`, order }));
    }

    // One at a time: the backend renumbers its siblings on every create, and
    // two creates racing would each renumber against a list missing the other.
    const tagOf = new Map<string, number>();
    for (const character of plan.characters) {
        tagOf.set(character.name, await deps.createCharacter(projectId, character));
    }
    for (const location of plan.locations) {
        tagOf.set(location, await deps.createLocation(projectId, location));
    }
    const tagsOf = (names: (string | null)[]) => names.flatMap(name => (name === null ? [] : (tagOf.get(name) ?? [])));

    for (const [index, scenes] of plan.episodes.entries()) {
        const fragmentId = fragmentIds[index];
        if (fragmentId === undefined) throw new Error(`Tutorial seed: no episode ${index + 1}`);
        for (const [sceneIndex, scene] of scenes.entries()) {
            await deps.createScene(projectId, {
                episodeId: structure.episodeId,
                fragmentId,
                position: (sceneIndex + 1) * POSITION_STEP,
                body: scene.body,
                tagIds: tagsOf([scene.location, ...scene.characters]),
            });
        }
    }
}
